import React, { useCallback, useEffect, useMemo, useState } from 'react';
import './Lightbox.css';

const buildImageFallbackSources = (url = '') => {
  const primary = String(url || '').trim();
  if (!primary) return [];
  const fallback = /^https?:\/\//i.test(primary)
    ? `https://images.hive.blog/0x0/${primary}`
    : '';
  return Array.from(new Set([primary, fallback].filter(Boolean)));
};

const Lightbox = ({ images = [], currentIndex = 0, onClose, onPrevious, onNext }) => {
  const hasImages = Array.isArray(images) && images.length > 0;

  const hasMultipleImages = hasImages && images.length > 1;
  const safeIndex = hasImages ? Math.max(0, Math.min(currentIndex, images.length - 1)) : 0;
  const activeImageOriginal = hasImages ? images[safeIndex] : '';
  const activeImageCandidates = useMemo(
    () => buildImageFallbackSources(activeImageOriginal),
    [activeImageOriginal]
  );
  const [sourceIndex, setSourceIndex] = useState(0);
  const activeImageSrc = activeImageCandidates[sourceIndex] || activeImageOriginal;

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose && onClose();
    }
  };

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Escape') {
      onClose && onClose();
      return;
    }

    if (!hasMultipleImages) return;

    if (e.key === 'ArrowLeft' && onPrevious) onPrevious();
    if (e.key === 'ArrowRight' && onNext) onNext();
  }, [hasMultipleImages, onClose, onPrevious, onNext]);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  useEffect(() => {
    setSourceIndex(0);
  }, [safeIndex, activeImageOriginal]);

  if (!hasImages) return null;

  return (
    <div className="lightbox-backdrop" onClick={handleBackdropClick}>
      <div className="lightbox-container">
        <button
          className="lightbox-close"
          onClick={onClose}
          title="Close (Esc)"
          aria-label="Close lightbox"
        >
          ✕
        </button>

        {hasMultipleImages && (
          <button
            className="lightbox-nav lightbox-prev"
            onClick={onPrevious}
            title="Previous (Left Arrow)"
            aria-label="Previous image"
          >
            ❮
          </button>
        )}

        <img
          src={activeImageSrc}
          alt={`Full size ${safeIndex + 1}`}
          className="lightbox-image"
          onError={() => {
            if (sourceIndex + 1 < activeImageCandidates.length) {
              setSourceIndex((prev) => prev + 1);
            }
          }}
        />

        {hasMultipleImages && (
          <button
            className="lightbox-nav lightbox-next"
            onClick={onNext}
            title="Next (Right Arrow)"
            aria-label="Next image"
          >
            ❯
          </button>
        )}

        {hasMultipleImages && (
          <div className="lightbox-counter">
            {safeIndex + 1} / {images.length}
          </div>
        )}
      </div>
    </div>
  );
};

export default Lightbox;
